import { useEffect, useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Row, Col, Form } from 'react-bootstrap'
import { useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Typography,
  InfoCard,
  FullPageContainer,
  ScrollableContainer,
} from '../components/styled'
import NotificationBoxPage from '../components/notificationBoxPage'
import FullLogo from '../assets/logos/full-logo.png'
import LanguagesDropdown from '../components/languageDropdwn'
import PrimaryButton from '../components/buttons/PrimaryButton'
import theme from '../constants/theme'
import apiSecureLinks from '../services/api/secureLinks'
import apiOfferLetters from '../services/api/offerLetter'
import storageApi from '../services/firebaseStorage'

const ACTION_STATES = { IDLE: 'idle', ACCEPT: 'accept', DECLINE: 'decline' }

const OFFER_RESPONSE_DATE_LOCALE = { ar: 'ar', gr: 'de-DE' }

function OfferResponse({ skipSecureLinkPrecheck = false } = {}) {
  const { t, i18n } = useTranslation()
  const { key } = useParams()

  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const [context, setContext] = useState(null)
  const [actionState, setActionState] = useState(ACTION_STATES.IDLE)
  const [joiningDate, setJoiningDate] = useState('')
  const [rejectionReason, setRejectionReason] = useState('')
  const [signedDocument, setSignedDocument] = useState(null)
  const [signedDocumentError, setSignedDocumentError] = useState('')
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [result, setResult] = useState(null)

  useEffect(() => {
    const load = async () => {
      try {
        if (!skipSecureLinkPrecheck) {
          await apiSecureLinks.getSingle(key)
        }
        const response = await apiOfferLetters.getCandidateResponseContext(key)
        setContext(response)
        if (response?.candidateJoiningDate)
          setJoiningDate(response.candidateJoiningDate)
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            t('offerResponse.invalidOrExpiredLink'),
        )
      } finally {
        setIsLoading(false)
      }
    }

    load()
  }, [key, t, skipSecureLinkPrecheck])

  const canSubmitAccept = useMemo(
    () => !!joiningDate && !!signedDocument && !isSubmitting,
    [joiningDate, signedDocument, isSubmitting],
  )

  const submit = async (action) => {
    setIsSubmitting(true)
    try {
      let payload
      if (action === 'approve') {
        const blobName = await storageApi.uploadFile(
          signedDocument,
          () => {},
          null,
          null,
        )
        payload = {
          action: 'approve',
          joining_date: joiningDate,
          signed_document_url: blobName,
        }
      } else {
        payload = { action: 'reject', rejection_reason: rejectionReason }
      }
      await apiOfferLetters.submitCandidateResponse(key, payload)
      setResult(action === 'approve' ? 'approved' : 'rejected')
    } catch (err) {
      setError(err?.response?.data?.message || t('offerResponse.submitFailed'))
    } finally {
      setIsSubmitting(false)
    }
  }

  const handleSignedDocumentChange = (e) => {
    const file = e.target.files?.[0]
    setSignedDocumentError('')
    if (!file) {
      setSignedDocument(null)
      return
    }
    if (file.type !== 'application/pdf') {
      setSignedDocumentError(t('offerResponse.signedDocumentInvalidFormat'))
      setSignedDocument(null)
      e.target.value = ''
      return
    }
    setSignedDocument(file)
  }

  if (isLoading) {
    return (
      <div
        className="d-flex justify-content-center align-items-center"
        style={{ minHeight: '100vh' }}
      >
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">{t('general.loading')}</span>
        </div>
      </div>
    )
  }

  if (error) return <NotificationBoxPage messageType="error" message={error} />

  if (result === 'approved') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('offerResponse.acceptedSuccess')}
      />
    )
  }

  if (result === 'rejected') {
    return (
      <NotificationBoxPage
        messageType="success"
        message={t('offerResponse.rejectedSuccess')}
      />
    )
  }

  if (context?.alreadyResponded) {
    return (
      <NotificationBoxPage
        messageType="warning"
        message={t('offerResponse.alreadyResponded')}
      />
    )
  }

  const dateLocale = OFFER_RESPONSE_DATE_LOCALE[i18n.language] ?? 'en-US'
  const expiryLabel = context?.offerExpiryDate
    ? new Date(context.offerExpiryDate).toLocaleDateString(dateLocale, {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
      })
    : null

  return (
    <FullPageContainer>
      <ScrollableContainer className="mt-3 fw-light">
        <Row className="my-2 align-items-end">
          <Col xs="auto">
            <img
              height="auto"
              width="150px"
              src={FullLogo}
              alt={t('offerResponse.logoAlt')}
            />
          </Col>
          <Col />
          <Col xs="auto">
            <LanguagesDropdown />
          </Col>
        </Row>
        <Row className="mt-4 pb-5 justify-content-center">
          <Col xs={12} md={8} lg={7}>
            <InfoCard className="px-4 pt-4 pb-4">
              {/* Header */}
              <div className="text-center mb-4">
                <Typography size="20px" bold color={theme.talentinoBlack}>
                  {t('offerResponse.jobOffer')}
                </Typography>
                {context?.companyName && (
                  <div className="mt-1">
                    <Typography
                      size="13px"
                      color={theme.talentinoSecondaryGray}
                    >
                      {t('offerResponse.fromCompany', {
                        company: context.companyName,
                      })}
                    </Typography>
                  </div>
                )}
              </div>

              {/* Greeting */}
              <div className="mb-3">
                <Typography size="14px" color={theme.talentinoTextGray}>
                  <strong>
                    {t('offerResponse.dearCandidate', {
                      name: context?.candidateName,
                    })}
                  </strong>
                </Typography>
              </div>

              <div className="mb-4">
                <Typography
                  size="14px"
                  color={theme.talentinoTextGray}
                  className="lh-lg"
                >
                  {t('offerResponse.offerBodyPart1')}
                  <strong>{context?.positionTitle}</strong>
                  {context?.companyName
                    ? t('offerResponse.atCompany', {
                        company: context.companyName,
                      })
                    : ''}
                  {t('offerResponse.offerBodyPart2')}
                </Typography>
              </div>

              {/* Offer Letter Document */}
              {context?.offerUrl && (
                <>
                  <div className="mb-2">
                    <Typography
                      size="12px"
                      bold
                      color={theme.talentinoSpecialBlue}
                    >
                      {t('offerResponse.offerLetterHeading')}
                    </Typography>
                  </div>
                  <AttachmentRow
                    label={t('offerResponse.offerLetterFileName', {
                      name: context?.candidateName?.replace(/\s/g, '_') || '',
                    })}
                    description={t('offerResponse.offerLetterDescription')}
                    url={context.offerUrl}
                    accentColor={theme.talentinoSpecialBlue}
                  />
                </>
              )}

              {/* Supplementary Attachment */}
              {context?.attachmentUrl && (
                <>
                  <div className="mb-2 mt-3">
                    <Typography
                      size="12px"
                      bold
                      color={theme.talentinoSecondaryGray}
                    >
                      {t('offerResponse.additionalDocumentHeading')}
                    </Typography>
                  </div>
                  <AttachmentRow
                    label={t('offerResponse.supplementaryFileName')}
                    description={t('offerResponse.supplementaryDescription')}
                    url={context.attachmentUrl}
                    accentColor={theme.talentinoSecondaryGray}
                  />
                </>
              )}

              {/* Expiry notice */}
              {expiryLabel && (
                <div
                  className="mt-3 p-2 text-center"
                  style={{
                    backgroundColor: '#FFF8E1',
                    borderRadius: '6px',
                    border: '1px solid #FFE082',
                  }}
                >
                  <Typography size="12px" color="#6D4C00">
                    {t('offerResponse.expiryValidUntil')}
                    <strong>{expiryLabel}</strong>
                    {t('offerResponse.expiryRespondBefore')}
                  </Typography>
                </div>
              )}

              <hr className="my-4" style={{ opacity: 0.1 }} />

              <div className="mb-3">
                <Typography size="13px" color={theme.talentinoTextGray}>
                  {t('offerResponse.reviewDecisionPrompt')}
                </Typography>
              </div>

              {/* Action Buttons - initial state */}
              {actionState === ACTION_STATES.IDLE && (
                <div className="d-flex gap-2">
                  <PrimaryButton
                    width="160px"
                    onClick={() => setActionState(ACTION_STATES.ACCEPT)}
                  >
                    {t('offerResponse.acceptOffer')}
                  </PrimaryButton>
                  <PrimaryButton
                    width="160px"
                    backgroundcolor={theme.talentinoDarkerRed}
                    hovercolor={theme.talentinoDarkerRed}
                    onClick={() => setActionState(ACTION_STATES.DECLINE)}
                  >
                    {t('offerResponse.declineOffer')}
                  </PrimaryButton>
                </div>
              )}

              {/* Accept flow - joining date step */}
              {actionState === ACTION_STATES.ACCEPT && (
                <div
                  className="p-3"
                  style={{
                    backgroundColor: '#F0F9F4',
                    borderRadius: '8px',
                    border: `1px solid ${theme.talentinoGreen}30`,
                  }}
                >
                  <div className="mb-2">
                    <Typography size="13px" bold color={theme.talentinoGreen}>
                      {t('offerResponse.acceptingOfferTitle')}
                    </Typography>
                  </div>
                  <Typography size="12px" color={theme.talentinoTextGray}>
                    {t('offerResponse.acceptingOfferHelp')}
                  </Typography>
                  <Form.Group className="mt-3 mb-3">
                    <Form.Label style={{ fontSize: '12px', color: '#6c757d' }}>
                      {t('offerResponse.joiningDateLabel')}
                    </Form.Label>
                    <Form.Control
                      type="date"
                      value={joiningDate}
                      onChange={(e) => setJoiningDate(e.target.value)}
                      style={{ fontSize: '13px', maxWidth: '250px' }}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <Form.Label style={{ fontSize: '12px', color: '#6c757d' }}>
                      {t('offerResponse.signedDocumentLabel')}
                      <span style={{ color: '#dc3545', marginLeft: '2px' }}>
                        *
                      </span>
                    </Form.Label>
                    <Form.Control
                      type="file"
                      accept="application/pdf"
                      onChange={handleSignedDocumentChange}
                      style={{ fontSize: '13px', maxWidth: '350px' }}
                      disabled={isSubmitting}
                    />
                    {signedDocumentError && (
                      <div
                        style={{
                          fontSize: '11px',
                          color: '#dc3545',
                          marginTop: '4px',
                        }}
                      >
                        {signedDocumentError}
                      </div>
                    )}
                    <div
                      style={{
                        fontSize: '11px',
                        color: '#9CA3AF',
                        marginTop: '4px',
                      }}
                    >
                      {t('offerResponse.signedDocumentHelp')}
                    </div>
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <PrimaryButton
                      width="180px"
                      backgroundcolor={theme.talentinoGreen}
                      hovercolor={theme.talentinoDarkerGreen}
                      onClick={() => submit('approve')}
                      disabled={!canSubmitAccept}
                    >
                      {isSubmitting
                        ? t('offerResponse.submitting')
                        : t('offerResponse.confirmAcceptance')}
                    </PrimaryButton>
                    <PrimaryButton
                      width="100px"
                      backgroundcolor="transparent"
                      hovercolor="transparent"
                      color={theme.talentinoTextGray}
                      border={`1px solid ${theme.talentinoWhiteBlue}`}
                      onClick={() => {
                        setActionState(ACTION_STATES.IDLE)
                        setJoiningDate(context?.candidateJoiningDate || '')
                        setSignedDocument(null)
                        setSignedDocumentError('')
                      }}
                      disabled={isSubmitting}
                    >
                      {t('general.back')}
                    </PrimaryButton>
                  </div>
                </div>
              )}

              {/* Decline flow - rejection reason step */}
              {actionState === ACTION_STATES.DECLINE && (
                <div
                  className="p-3"
                  style={{
                    backgroundColor: '#FDF2F2',
                    borderRadius: '8px',
                    border: `1px solid ${theme.talentinoDarkerRed}20`,
                  }}
                >
                  <div className="mb-2">
                    <Typography
                      size="13px"
                      bold
                      color={theme.talentinoDarkerRed}
                    >
                      {t('offerResponse.decliningOfferTitle')}
                    </Typography>
                  </div>
                  <Typography size="12px" color={theme.talentinoTextGray}>
                    {t('offerResponse.decliningOfferHelp')}
                  </Typography>
                  <Form.Group className="mt-3 mb-3">
                    <Form.Label style={{ fontSize: '12px', color: '#6c757d' }}>
                      {t('offerResponse.reasonLabel')}
                    </Form.Label>
                    <Form.Control
                      as="textarea"
                      rows={3}
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      placeholder={t('offerResponse.reasonPlaceholder')}
                      style={{ fontSize: '13px', resize: 'none' }}
                    />
                  </Form.Group>
                  <div className="d-flex gap-2">
                    <PrimaryButton
                      width="180px"
                      backgroundcolor={theme.talentinoDarkerRed}
                      hovercolor={theme.talentinoRed}
                      onClick={() => submit('reject')}
                      disabled={isSubmitting}
                    >
                      {isSubmitting
                        ? t('offerResponse.submitting')
                        : t('offerResponse.confirmDecline')}
                    </PrimaryButton>
                    <PrimaryButton
                      width="100px"
                      backgroundcolor="transparent"
                      hovercolor="transparent"
                      color={theme.talentinoTextGray}
                      border={`1px solid ${theme.talentinoWhiteBlue}`}
                      onClick={() => setActionState(ACTION_STATES.IDLE)}
                      disabled={isSubmitting}
                    >
                      {t('general.back')}
                    </PrimaryButton>
                  </div>
                </div>
              )}
            </InfoCard>
          </Col>
        </Row>
      </ScrollableContainer>
    </FullPageContainer>
  )
}

function AttachmentRow({ label, description, url, accentColor }) {
  const { t } = useTranslation()
  return (
    <div
      className="p-3 mb-2 d-flex align-items-center justify-content-between"
      style={{
        border: `1px solid ${accentColor}25`,
        borderLeft: `3px solid ${accentColor}`,
        borderRadius: '6px',
        backgroundColor: '#FAFBFC',
      }}
    >
      <div
        className="d-flex align-items-center"
        style={{ minWidth: 0, flex: 1 }}
      >
        <div
          className="me-3 d-flex align-items-center justify-content-center"
          style={{
            width: '36px',
            height: '36px',
            borderRadius: '6px',
            backgroundColor: `${accentColor}12`,
            flexShrink: 0,
          }}
        >
          <svg
            width="18"
            height="18"
            viewBox="0 0 24 24"
            fill="none"
            stroke={accentColor}
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
            <polyline points="10 9 9 9 8 9" />
          </svg>
        </div>
        <div style={{ minWidth: 0 }}>
          <div
            style={{
              fontSize: '13px',
              fontWeight: 500,
              color: '#1a1a1a',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </div>
          <div style={{ fontSize: '11px', color: '#9CA3AF' }}>
            {description}
          </div>
        </div>
      </div>
      <div
        className="d-flex align-items-center gap-2 ms-3"
        style={{ flexShrink: 0 }}
      >
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          style={{
            fontSize: '12px',
            fontWeight: 500,
            color: accentColor,
            textDecoration: 'none',
            cursor: 'pointer',
            padding: '4px 10px',
            borderRadius: '4px',
            border: `1px solid ${accentColor}30`,
            transition: 'background-color 0.15s',
          }}
          onMouseEnter={(e) =>
            (e.target.style.backgroundColor = `${accentColor}10`)
          }
          onMouseLeave={(e) => (e.target.style.backgroundColor = 'transparent')}
        >
          {t('general.view')}
        </a>
      </div>
    </div>
  )
}

OfferResponse.propTypes = {
  skipSecureLinkPrecheck: PropTypes.bool,
}

OfferResponse.defaultProps = {
  skipSecureLinkPrecheck: false,
}

AttachmentRow.propTypes = {
  label: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  url: PropTypes.string.isRequired,
  accentColor: PropTypes.string.isRequired,
}

export default OfferResponse
